import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type { AcrBulkRescanResponse, AcrResultsResponse } from "../../types/acr";

/**
 * プロジェクトメンバー向け ACR Cloud 結果一覧 / 一括再スキャン API。
 *
 * - `GET  /api/me/:project_id/acr-results` プロジェクト内 ACR 結果一覧
 * - `POST /api/me/:project_id/acr-rescan`  照合中(processing)の一括再スキャン
 *
 * 結果一覧は管理者向け `adminAcrApi` と同一のレスポンス形（ショット内メディア /
 * ショットコメント添付 / トークルーム添付の 3 区分）を返す。権限は
 * バックエンドの `ProjectPermissionMixin` により制限される:
 * - 一覧取得: プロデューサーグループ + クライアント責任者（owner / producer /
 *   admin、加えて master_admin）。
 * - 一括再スキャン: プロデューサーグループ（producer / admin、加えて
 *   master_admin）のみ。クライアント責任者（owner）は不可。
 *
 * プロジェクト INFO 内「楽曲照合結果」ページから利用する。
 */
export const projectAcrApi = createApi({
  reducerPath: "projectAcrApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["ProjectAcrResults"],
  endpoints: (builder) => ({
    getProjectAcrResults: builder.query<AcrResultsResponse, string>({
      query: (projectId) => `/api/me/${projectId}/acr-results`,
      providesTags: (_result, _error, projectId) => [
        { type: "ProjectAcrResults", id: projectId },
      ],
    }),

    bulkRescanProjectAcr: builder.mutation<AcrBulkRescanResponse, string>({
      query: (projectId) => ({
        url: `/api/me/${projectId}/acr-rescan`,
        method: "POST",
      }),
      // 再スキャンは非同期（Celery）なので即時に状態が変わるとは限らないが、
      // 一覧キャッシュを無効化し、refetch / 再訪問で最新ステータスを取得できるようにする。
      invalidatesTags: (_result, _error, projectId) => [
        { type: "ProjectAcrResults", id: projectId },
      ],
    }),
  }),
});

export const {
  useGetProjectAcrResultsQuery,
  useBulkRescanProjectAcrMutation,
} = projectAcrApi;
